import React, { createRef } from "react";
import ReactDOM from "react-dom";
import debounce from "lodash.debounce";
import _ from "lodash";
import { vec3 } from "gl-matrix";
import cx from "classnames";
import { inject, observer } from "mobx-react";
/// sub components
import DCMTagMarkerContainer from "pages/viewer-dashboard/container/viewLayout/DCMTagMarkerContainer";
import OrientationMarkerContainer from "pages/viewer-dashboard/container/viewLayout/OrientationMarkerContainer";
import { ContextMenu, MenuItem, ContextMenuTrigger } from "react-contextmenu";
import Cache from "core/cache/index";

/// functions
import { getDefaultViewport } from "./ViewportConfig.js";
import renderToCanvas from "component/molecules/viewport/core/render/renderToCanvas.js";
import resize from "component/molecules/viewport/core/canvas/resize.js";
import { default as mouseWheelInput } from "component/molecules/viewport/interfaces/mouse-wheel-input.js";
import BuiltInTool from "./tools";
import ColorBox from "component/atoms/colorBox";
import {
  getEnabledElement,
  removeEnabledElement,
  redrawAllEnabledElement,
  addEnabledElement,
  hasEnabledElement,
  updateEnabledElement,
} from "component/toolManager/enabledElements.js";
import {
  addEnabledElementToolStore,
  removeEnabledElementToolStore,
} from "component/molecules/viewport/toolStates/addEnabledElement.js";
import {
  addToolGlobally,
  addToolForElement,
  removeToolForElement,
} from "component/molecules/viewport/toolStates/addTool.js";
import getToolForElement from "component/molecules/viewport/toolStates/getToolForElement";

import {
  setToolEnabled,
  setToolDisabled,
  setToolDisabledForElement,
} from "component/molecules/viewport/toolStates/setToolMode";
import { clearAll } from "component/toolManager/toolState";
import {
  getToolState,
  clearElementState,
} from "component/toolManager/toolState.js";

import math from "core/math";
import { removeToolState } from "component/toolManager/toolState.js";

import getStoredPixels from "component/molecules/viewport/core/lookupTable/getStoredPixels.js";
import { default as getRGBPixels } from "component/molecules/viewport/core/lookupTable/getRGBPixels.js";

/// message dispatcher
import FloatMessageBox from "core/messageBoxController/index";
import pubsub from "global/global";
import CustomEvent from "core/customEvent/index";
import {
  MouseEventDispatcher,
  ToolEventDispatcher,
  ImageRenderedEventDispatcher,
} from "./core/eventDispatcher";

import getToolsForElement from "component/molecules/viewport/toolStates/getToolsForElement.js";
import { getters } from "component/molecules/viewport/toolStates/store.js";
/// load measurement toool
import {
  Crosshair3DTool,
  ImageFusionTool,
  CobbAngleTool,
  AngleTool,
  FreeformShapeTool,
  EllipseRoiTool,
  ScaleOverlayTool,
  LengthTool,
  RectangleRoiTool,
} from "component/molecules/viewport/tools/annotation";

// sync function disable
import ReferenceLine from "component/molecules/viewport/tools/annotation/ReferenceLinesTool";
import Config from "config";
/// Helper
import Helper from "helper/index";
import Math from "core/math";
// Constants
import Constants from "constants/index";

import Canvas from "component/molecules/viewport/core/canvas/index.js";
import triggerEvent from "core/customEvent/triggerEvent.js";
import Validator from "helper/validator/index";
import createMPRImage from "./core/MPR/vtk/createMPRImage.js";

import "./Viewport.styl";

let cursorPageX = 0;
let cursorPageY = 0;

/**
 * @description
 * @function getPixelValue
 * @param x
 * @param y
 * @param z
 */
function getPixelValue(x, y, element, isColor) {
  const enabledElement = getEnabledElement(element);
  const image = enabledElement.image;

  if (!_.get(image, "columns")) {
    return "";
  }

  if (x < 0 || x >= image.columns || y < 0 || y >= image.rows) {
    return "";
  }

  let text = "";
  if (isColor) {
    const storedPixels = getRGBPixels(element, x, y, 1, 1);
    if (storedPixels === null || storedPixels[0] === undefined) {
      return "";
    }
    //text = `R: ${storedPixels[0]} G: ${storedPixels[1]} B: ${storedPixels[2]}`;
    text = `Val : ${storedPixels[0]},${storedPixels[1]},${storedPixels[2]}`;
  } else {
    const storedPixels = getStoredPixels(element, x, y, 1, 1);
    if (storedPixels === null || storedPixels[0] === undefined) {
      return "";
    }
    const value = storedPixels[0];

    let mo = value * image.rescaleSlope + image.rescaleIntercept;
    if (Number.isInteger(mo) === true) {
      mo = mo.toFixed(0);
    } else if (mo < 1 && mo > 0) {
      mo = mo.toFixed(7);
    } else {
      mo = mo.toFixed(3);
    }
    //text = "Val : " + value;
    text = "Val : " + mo;
  }

  return text;
}
/**
 * Create a canvas or returns the one that already exists for a given element
 *
 * @param {HTMLElement} element An HTML Element
 * @return {HTMLElement} canvas A Canvas DOM element
 */
function getCanvas(element) {
  const selector = `canvas.viewer-canvas`;

  return element.querySelector(selector);
}

/**
 * @description Viewport UI Class
 *
 * @class Viewport
 * @extends {React.Component<IViewportProps, any>}
 */
@inject("toolbarStore")
@inject("gridLayoutStore")
@inject("synchronizationStore")
@observer
class Viewport extends React.Component {
  /**
   *  constants
   */
  MOUSE_COORDINATE_UPDATE_THROTTLE_TIME = 40;

  element = createRef();
  requestUpdateFrame = null;
  frameReady = true; // true -> 렌더링 준비 상태. false -> 렌더링 준비되지 않음(이전 렌더링작업이 종료되지않은경우)
  pendingTick = false; // true -> 이전 Tick가 끝나지 않아, 현재까지 작업이 밀림상태 false -> 계류중인 작업 없음
  toolEventDispatcher = new ToolEventDispatcher();
  mouseEventDispatcher = new MouseEventDispatcher();
  previousScrollInView = false;
  /**
   * subscriber IDs
   *
   * @private
   * @memberof Viewport
   */
  setPresetWindowSubscriberId = -1;
  renderInvertSubscriberId = -1;
  setDefaultWindowSubscriberId = -1;
  setDynamicWindowSubscriberId = -1;
  setScrollIndexAddId = -1;
  setViewPositionInitialize = -1;
  /**
   * default property values
   *
   * @static
   * @memberof Viewport
   */
  static defaultProps = {
    isActive: false,
    defaultFrameIdx: 0,
    frames: 0,
    layoutString: "",
    isMPR: false,
    isDisplayDCMTags: true,
    isDisplayOrientationMarkers: true,
    hasContextMenu: true,
    focus: false,
    // frameRangeMin: -1,
    // frameRangeMax: Number.MAX_VALUE,
    displayRange: [-1, Number.MAX_VALUE],
    showTagOverlay: true,
    onInitCallback: () => {},
  };

  /**
   *Creates an instance of Viewport.
   * @param {*} props
   * @memberof Viewport
   */
  constructor(props) {
    super(props);
    this.state = {
      currentImageIdx:
        this.data !== null || this.data !== undefined
          ? props.defaultFrameIdx
          : 0,
      prevWidthRatio: 0,
      prevHeightRatio: 0,
      mouseXPos: -1, //
      mouseYPos: -1,
      presetLevelName: "",
    };
    this.updateAnimationState = this.updateAnimationState.bind(this);
  }

  /**
   * @description 컴포넌트가 정상 마운트되었을때, 필요한 이벤트 핸들러들을 등록한다.
   * @method componentDidMount
   * @memberof Viewport
   */
  componentDidMount() {
    const { isEnableScalebarTool } = Config.privateSupport;
    ///
    /// 1. 이벤트 리스너 등록
    ///
    this.setPresetWindowSubscriberId = pubsub.subscribe(
      CustomEvent.Messages.CONTRAST_VIEWPORT,
      this.handleSetPresetContrast
    );

    this.setScrollIndexAddId = pubsub.subscribe(
      CustomEvent.Messages.SET_SCROLL_INDEX_ADD,
      this.handleSetScrollIndexAdd
    );

    this.setViewPositionInitialize = pubsub.subscribe(
      CustomEvent.Messages.SET_VIEW_POSITION_INITIALIZE,
      this.handleSetViewPositionInitialize
    );

    this.renderInvertSubscriberId = pubsub.subscribe(
      CustomEvent.Messages.RENDER_INVERT,
      this.handleRequestRenderInvert
    );
    this.setDefaultWindowSubscriberId = pubsub.subscribe(
      CustomEvent.Messages.CONTRAST_DEFAULT,
      this.handleContrastDefault
    );
    this.setDynamicWindowSubscriberId = pubsub.subscribe(
      CustomEvent.Messages.CONTRAST_FULL_DYNAMIC,
      this.handleContrastFullDynamic
    );

    window.addEventListener("resize", this.handleWindowResize);
    this.element.addEventListener(
      CustomEvent.EVENTS.MOUSE_WHEEL,
      this.handleStackScroll
    );
    this.element.addEventListener("keydown", this.handleKeyDown.bind(this));
    this.element.addEventListener(
      "mousedown",
      this.handleMouseClick.bind(this)
    );
    this.element.addEventListener(
      "dblclick",
      this.handleMouseDblClick.bind(this)
    );
    this.element.addEventListener(
      "mousewheel",
      this.handleMouseWheel.bind(this)
    );
    this.element.addEventListener(
      "onChangeScrollbar",
      this.handleChangeScroll.bind(this)
    );
    this.element.addEventListener(
      "forceUpdate",
      this.handleForceUpdate.bind(this)
    );

    mouseWheelInput.enable(this.element);
    ImageRenderedEventDispatcher.activate(this.element);
    this.mouseEventDispatcher.enableMouseEventListen(this.element);
    this.toolEventDispatcher.setupAvailableTools(BuiltInTool);
    this.toolEventDispatcher.activateDispatcher(this.element);

    ///
    /// Register measurement Tool
    ///
    let metadataCache = new Cache.Metadata();
    const image = metadataCache.getFrame(this.props.seriesID, 0);
    //const image = metadataCache.getFrame(this.props.data, 0);

    const { identifier, isMPR } = this.props;
    const enabledElement = {
      identifier,
      element: this.element,
      canvas: getCanvas(this.element),
      viewport: getDefaultViewport(null, undefined),
      image: image,
      imageIdx:
        this.data !== null || this.data !== undefined
          ? this.props.defaultFrameIdx
          : 0,
      invalid: false, // True if image needs to be drawn, false if not
      needsRedraw: true,
      options: null,
      layers: [],
      data: {},
      renderingTools: {},
      uuid: Helper.UUID.generateUUID(),
      isDataChanging: false,
    };

    if (!hasEnabledElement(this.element)) {
      addEnabledElement(enabledElement);
      addEnabledElementToolStore(enabledElement.element);
    }

    resize(this.element, true);

    addToolGlobally(LengthTool);
    addToolForElement(this.element, LengthTool);
    addToolGlobally(RectangleRoiTool);
    addToolForElement(this.element, RectangleRoiTool);
    addToolGlobally(EllipseRoiTool);
    addToolForElement(this.element, EllipseRoiTool);
    addToolGlobally(FreeformShapeTool);
    addToolForElement(this.element, FreeformShapeTool);
    addToolGlobally(AngleTool);
    addToolForElement(this.element, AngleTool);
    addToolGlobally(CobbAngleTool);
    addToolForElement(this.element, CobbAngleTool);
    addToolGlobally(ReferenceLine);
    addToolForElement(this.element, ReferenceLine);
    // addToolGlobally(Crosshair3DTool);
    //addToolForElement(this.element, Crosshair3DTool);

    if (isEnableScalebarTool === true) {
      addToolForElement(this.element, ScaleOverlayTool);
      setToolEnabled("ScaleOverlayTool", {});
    }

    if (isMPR === false) {
      // auto generation
      setToolEnabled("ReferenceLines", {
        synchronizationContext:
          Config.rootStore.synchronizationStore._updateImageSync,
      });
    }
    if (Config.privateSupport.isEnableImageFusion === true) {
      addToolGlobally(ImageFusionTool);
      addToolForElement(this.element, ImageFusionTool);
    }
    // auto generation
    // setToolEnabled("ImageFusion", {
    //   synchronizationContext:
    //     Config.rootStore.synchronizationStore._slicePositionSync,
    // });

    this.element.addEventListener(
      "mousemove",
      _.throttle(
        this.handleViewportMousemove,
        this.MOUSE_COORDINATE_UPDATE_THROTTLE_TIME
      )
    );

    this.requestUpdateFrame = requestAnimationFrame(
      this.beforeUpdateAnimationState
    );
  }

  /**
   * @description
   * @method componentDidUpdate
   * @memberof Viewport
   */
  componentDidUpdate(prevProps, prevState) {
    //let { data, frameRangeMin, frameRangeMax } = this.props;
    let { data, displayRange, isMPR, imageOrientation, frames } = this.props;

    let enabledElement = getEnabledElement(this.element);
    let metadataCache = new Cache.Metadata();
    if (prevProps.focus !== this.props.focus) {
      this.setState({});
    }

    // FIXME
    if (!!prevProps.data === false && !!data === true) {
      this.setState({
        currentImageIdx: this.props.defaultFrameIdx,
      });
    }

    if (data === null) {
      //enabledElement.imageIdx = 0;
    } else if (prevProps.defaultFrameIdx !== this.props.defaultFrameIdx) {
      let defaultFrameIdx = Math.min(
        Math.max(this.props.defaultFrameIdx, displayRange["min"]),
        displayRange["max"]
      );
      //enabledElement.imageIdx = this.props.defaultFrameIdx;
      enabledElement.imageIdx = defaultFrameIdx;
      enabledElement.needsRedraw = true;
    }

    let imageIndex =
      enabledElement.imageIdx === -1 ? 0 : enabledElement.imageIdx;

    //if (_.isEqual(imageOrientation, [1, 0, 0, 0, 1, 0]) === true) {
    //if (_.isEqual(imageOrientation, [1, 0, 0, 0, 1, 0]) === true) {
    if (isMPR === false) {
      let frame = metadataCache.getFrame(data, imageIndex);
      enabledElement.image = frame;
      if (
        !!frame === true &&
        (data !== prevProps.data || prevProps.frames !== frames)
      ) {
        enabledElement.imageIdx = imageIndex;
        enabledElement.viewport.displayedArea.brhc.x = frame.columns;
        enabledElement.viewport.displayedArea.brhc.y = frame.rows;
        enabledElement.viewport.translation.x = 0;
        enabledElement.viewport.translation.y = 0;
        enabledElement.viewport.rotation = 0;
        enabledElement.viewport.scale = 1;
        resize(this.element, true);
      }

      this.state.currentImageIdx = imageIndex;
      // auto generation
      setToolEnabled("ReferenceLines", {
        synchronizationContext:
          Config.rootStore.synchronizationStore._updateImageSync,
      });
    } else {
      setToolDisabled("ReferenceLines", {
        synchronizationContext:
          Config.rootStore.synchronizationStore._updateImageSync,
      });
    }
    if (enabledElement.image) {
      resize(this.element, false, true);
    } else {
      resize(this.element, true);
    }

    enabledElement.needsRedraw = true;
  }

  /**
   * @description 모든 메시지 핸들러를 해제
   * @method ComponentWillUnmount
   * @memberof Viewport
   */
  componentWillUnmount = () => {
    // RAF 핸들러 해제
    cancelAnimationFrame(this.requestUpdateFrame);

    // 마우스 휠 이벤트 핸들러 해제
    mouseWheelInput.disable(this.element);

    // 브라우저 크기 조절 대응 핸들러 해제
    window.removeEventListener("resize", this.handleWindowResize);

    // DCMView Element에 지정한 이벤트 해제
    this.element.removeEventListener("mousemove", this.handleViewportMousemove);
    this.element.removeEventListener(
      CustomEvent.EVENTS.MOUSE_WHEEL,
      this.handleStackScroll
    );
    this.element.removeEventListener("keydown", this.handleKeyDown.bind(this));
    this.element.removeEventListener(
      "mousedown",
      this.handleMouseClick.bind(this)
    );
    this.element.removeEventListener(
      "dblclick",
      this.handleMouseDblClick.bind(this)
    );
    this.element.removeEventListener(
      "mousewheel",
      this.handleMouseWheel.bind(this)
    );
    this.element.removeEventListener(
      "onChangeScrollbar",
      this.handleChangeScroll.bind(this)
    );
    this.element.removeEventListener(
      "forceUpdate",
      this.handleForceUpdate.bind(this)
    );
    ///

    pubsub.unsubscribe(this.setPresetWindowSubscriberId);
    pubsub.unsubscribe(this.renderInvertSubscriberId);
    pubsub.unsubscribe(this.setDefaultWindowSubscriberId);
    pubsub.unsubscribe(this.setViewPositionInitialize);
    pubsub.unsubscribe(this.handleSetScrollIndexAdd);
    pubsub.unsubscribe(this.setScrollIndexAddId);
    pubsub.unsubscribe(this.setDynamicWindowSubscriberId);

    removeToolForElement(this.element, "length");
    removeToolForElement(this.element, "rectangle");
    removeToolForElement(this.element, "ellipse");
    removeToolForElement(this.element, "pencil");
    removeToolForElement(this.element, "angle");
    removeToolForElement(this.element, "cobb-angle");
    removeToolForElement(this.element, "ScaleOverlayTool");
    removeToolForElement(this.element, "ImageFusionTool");
    removeEnabledElement(this.element);
    removeEnabledElementToolStore(this.element);
    setToolDisabledForElement(this.element, "ImageFusion", {
      synchronizationContext:
        Config.rootStore.synchronizationStore._updateImageSync,
    });
    this.toolEventDispatcher.deactivateDispatcher(this.element);
    this.mouseEventDispatcher.disableMouseEventListen(this.element);
    ImageRenderedEventDispatcher.deactivate(this.element);
  };

  handleForceUpdate = (event) => {
    this.forceUpdate();
  };

  beforeUpdateAnimationState = () => {
    if (this.frameReady === false) {
      this.pendingTick = true;
      this.requestUpdateFrame = requestAnimationFrame(
        this.beforeUpdateAnimationState
      );
      return;
    }
    this.updateAnimationState(this);
  };
  updateAnimationState(that) {
    that.frameReady = false;
    const ret = that.renderCanvasAsync();
    if (ret !== null) {
      ret.then((res) => {
        that.afterUpdateAnimationState();
      });
    } else {
      that.frameReady = true;
    }

    that.requestUpdateFrame = requestAnimationFrame(
      that.beforeUpdateAnimationState
    );
  }

  handleMouseWheel = (event) => {
    const { gridLayoutStore, displayRange } = this.props;
    const totalPages = gridLayoutStore.getLayoutTotalPages();
    const frames = displayRange["max"] - displayRange["min"] + 1;
    /// HACK : 전체 페이지 수가 1보다 클 때, 페이지 스크롤이 됨
    /// 추후 나은 방법으로 수정 필요
    if (totalPages === 1) {
      event.preventDefault();
    } else {
      // 영상이 한장 이상이면, DCMView에 대한 스크롤 이벤트만 발생
      // (페이지 스크롤 차단)
      if (frames > 1) {
        event.preventDefault();
      }
    }
  };

  /**
   * @description DCMView 영역 내에서 발생하는 마우스 이동 이벤트 처리 핸들러
   * @method handleViewportMousemove
   * @param {MouseEvent} event  마우스 이벤트 정보
   * @memberof Viewport
   */
  handleViewportMousemove = (event) => {
    const { data } = this.props;

    const enabledElement = getEnabledElement(this.element);
    cursorPageX = event.pageX;
    cursorPageY = event.pageY;
    ///
    /// 1. Canvas 가 정상적으로 생성되지 않았거나(Element 생성 문제 또는 가시화되지 않은상태)
    /// DCMView에 할당된 데이터가 없는 경우, 함수를 반환하고 종료
    ///
    if (
      Helper.Validator.has(enabledElement, "canvas") === false ||
      Helper.Validator.has(enabledElement, "image") === false
    ) {
      return;
    }

    ///
    /// 2. 현재 설정된 스크롤 위치에 해당하는 영상 획득
    ///

    let scrollPos =
      enabledElement.imageIdx === -1 ? 0 : enabledElement.imageIdx;
    let metadataCache = new Cache.Metadata();
    const image = metadataCache.getFrame(data, scrollPos);

    ///
    /// 3. 현재 설정된 스크롤 위치에 해당하는 영상 획득
    ///
    if (
      Helper.Validator.has(image, "columns") === false ||
      Helper.Validator.has(image, "rows") === false
    ) {
      return;
    }

    ///
    /// 4. 마우스 좌표를 영상 좌표계로 변환
    ///
    const mousePos = math.coordinate.pageToPixel(
      this.element,
      event.pageX,
      event.pageY
    );
    const text = getPixelValue(
      mousePos.x,
      mousePos.y,
      this.element,
      image.color
    );
    //console.log(text)
    ///
    /// 5. 영상 내 마우스 좌표가 위치한다면, DCMView에 출력.
    /// 영상 내 위치하지 않으면, 함수를 반환하고 종료
    ///
    const viewportCoord = {
      x: 0,
      y: 0,
      width: image.columns,
      height: image.rows,
    };
    if (this.tagInnerMarker !== undefined && this.tagInnerMarker !== null) {
      if (
        math.intersection.pointInRectangle(mousePos, viewportCoord) === false
      ) {
        triggerEvent(this.tagInnerMarker.element, "changedPointOfInterest", {
          x: -1,
          y: -1,
          value: "",
        });
      } else {
        triggerEvent(this.tagInnerMarker.element, "changedPointOfInterest", {
          x: mousePos.x,
          y: mousePos.y,
          value: text,
        });
      }
    }
  };

  afterUpdateAnimationState = () => {
    this.frameReady = true;
    if (this.pendingTick) {
      this.pendingTick = false;
    }
  };

  /**
   * @description 화면 크기 변경시, viewport 크기 변환
   * @method debouncedResize
   * @memberof Viewport
   */
  debouncedResize = debounce(() => {
    if (this.element === null || this.element === undefined) return;

    if (
      this.element.clientHeight === null ||
      this.element.clientHeight === undefined
    ) {
      return;
    }

    let enabledElement = getEnabledElement(this.element);

    if (!_.get(enabledElement, "image")) {
      return;
    }

    const hasImage = _.get(enabledElement, "image");

    if (!hasImage) {
      resize(this.element, true);
    } else {
      resize(this.element);
    }
    enabledElement.needsRedraw = true;
  }, 250);

  /**
   *
   *
   * @memberof Viewport
   */
  handleKeyDown = (event) => {
    if (event.keyCode === 46) {
      let tools = getToolsForElement(this.element, getters.mouseTools());
      let imageCorod = math.coordinate.pageToPixel(
        this.element,
        cursorPageX,
        cursorPageY
      );
      let coords = math.coordinate.pixelToCanvas(this.element, imageCorod);

      // NEAR TOOL?
      const annotationToolsWithPointNearClick = tools.filter(
        function (tool) {
          const toolState = getToolState(this.element, tool.name);
          const isNearPoint =
            toolState &&
            toolState.data &&
            tool.pointNearTool &&
            toolState.data.some((data) =>
              tool.pointNearTool(this.element, data, coords, "mouse")
            );

          return isNearPoint;
        }.bind(this)
      );

      if (annotationToolsWithPointNearClick.length > 0) {
        const firstToolNearPoint = annotationToolsWithPointNearClick[0];
        const toolState = getToolState(this.element, firstToolNearPoint.name);
        const firstAnnotationNearPoint = toolState.data.find((data) =>
          firstToolNearPoint.pointNearTool(this.element, data, coords)
        );

        removeToolState(
          this.element,
          firstToolNearPoint.name,
          firstAnnotationNearPoint
        );
        this.setState(this.state);

        let enabledElement = getEnabledElement(this.element);
        enabledElement.needsRedraw = true;

        return;
      }
    }
  };

  /**
   * @description Contrast Preset 적용
   * @deprecated
   * @method handleSetPresetContrast
   * @memberof Viewport
   */
  handleSetPresetContrast = (payload, msg) => {
    const { data, isActive } = this.props;
    if (isActive === false) {
      return;
    }

    if (data === null || data === undefined) {
      return;
    }

    let enabledElement = getEnabledElement(this.element);
    const type = payload["type"];
    if (type === "window") {
      enabledElement.viewport.voi.windowCenter = payload["value"]["wl"];
      enabledElement.viewport.voi.windowWidth = payload["value"]["ww"];
    } else if (type === "displayedmaximumvalue") {
      const percent = payload["value"];

      let metadataCache = new Cache.Metadata();
      const seriesMinMax = metadataCache.getSeriesMinMax(this.props.data);
      //const maxPixelValue = enabledElement.image.minMax[1];
      const maxPixelValue = seriesMinMax["max"];
      //const slope = enabledElement.image.rescaleSlope;
      const seriesMaxRescaleSlope = metadataCache.getSeriesMaxRescaleSlope(
        this.props.data
      );
      if (seriesMaxRescaleSlope === null) {
        return;
      }

      const supremumUnit =
        ((maxPixelValue * percent) / 100) * seriesMaxRescaleSlope;
      const infimumUnit = 0 * seriesMaxRescaleSlope;

      //const intercept = enabledElement.image.rescaleIntercept;
      // const supremumUnit =
      //   ((maxPixelValue * percent) / 100) * seriesMaxRescaleSlope + intercept;
      //const infimumUnit = 0 * seriesMaxRescaleSlope + intercept;
      const windowCenter = (supremumUnit + infimumUnit) / 2;
      const windowWidth = Math.abs(supremumUnit - infimumUnit);
      enabledElement.viewport.voi.windowCenter = windowCenter;
      enabledElement.viewport.voi.windowWidth = windowWidth;
    }
    this.state.presetLevelName = payload["name"] || "";

    this.setState(this.state);
    enabledElement.needsRedraw = true;

    CustomEvent.triggerEvent(
      this.element,
      CustomEvent.EVENTS.WWWC_CHANGED,
      enabledElement
    );
  };

  handleRequestRenderInvert = (payload, msg) => {
    if (this.props.isActive === false) {
      return;
    }
    let enabledElement = getEnabledElement(this.element);
    enabledElement.viewport.invert = !enabledElement.viewport.invert;

    this.setState(this.state);

    enabledElement.needsRedraw = true;
    // pubsub.publish(CustomEvent.Messages.CONTRAST_DROPDOWN_RENDER, {
    //   selectedValue: "ct-Negative"
    // });
    CustomEvent.triggerEvent(
      this.element,
      CustomEvent.EVENTS.WWWC_CHANGED,
      enabledElement
    );
  };

  handleContrastDefault = (payload, msg) => {
    const { data, isActive } = this.props;
    if (isActive === false) {
      return;
    }

    let frameIndex =
      this.state.currentImageIdx === -1 ? 0 : this.state.currentImageIdx;
    if (data === null || data === undefined) {
      return;
    }

    let metadataCache = new Cache.Metadata();
    const image = metadataCache.getFrame(data, frameIndex);

    let enabledElement = getEnabledElement(this.element);
    enabledElement.viewport.voi.windowCenter = image.windowCenter;
    enabledElement.viewport.voi.windowWidth = image.windowWidth;
    this.state.presetLevelName = "D";

    const modality = _.get(image, "modality", "");
    // PT 인경우, 25% 고정
    if (modality.toLowerCase() === "pt") {
      const percent = 25;

      const seriesMinMax = metadataCache.getSeriesMinMax(this.props.data);
      //const maxPixelValue = enabledElement.image.minMax[1];
      const maxPixelValue = seriesMinMax["max"];
      //const slope = enabledElement.image.rescaleSlope;
      const seriesMaxRescaleSlope = metadataCache.getSeriesMaxRescaleSlope(
        this.props.data
      );
      if (seriesMaxRescaleSlope === null) {
        return;
      }

      const supremumUnit =
        ((maxPixelValue * percent) / 100) * seriesMaxRescaleSlope;
      const infimumUnit = 0 * seriesMaxRescaleSlope;

      const windowCenter = (supremumUnit + infimumUnit) / 2;
      const windowWidth = Math.abs(supremumUnit - infimumUnit);
      enabledElement.viewport.voi.windowCenter = windowCenter;
      enabledElement.viewport.voi.windowWidth = windowWidth;
    }
    //
    this.setState(this.state);
    enabledElement.needsRedraw = true;

    CustomEvent.triggerEvent(
      this.element,
      CustomEvent.EVENTS.WWWC_CHANGED,
      enabledElement
    );
  };

  /**
   * @method handleContrastFullDynamic
   *
   * @ref @link "https://www.radiantviewer.com/dicom-viewer-forum/calculation-full-dynamic-window-values/368/"
   * @memberof Viewport
   */
  handleContrastFullDynamic = (payload, msg) => {
    const { data, isActive } = this.props;
    if (isActive === false) {
      return;
    }

    if (data === null || data === undefined) {
      return;
    }

    let frameIndex =
      this.state.currentImageIdx === -1 ? 0 : this.state.currentImageIdx;

    let metadataCache = new Cache.Metadata();
    const image = metadataCache.getFrame(data, frameIndex);

    if (Validator.isDefined(image) === false) {
      return;
    }

    // WW = vmax - vmin
    const ww = image.minMax[1] - image.minMax[0];
    const wl = image.minMax[0] + image.windowWidth / 2;
    // WL = vmin + WW / 2
    let enabledElement = getEnabledElement(this.element);
    enabledElement.viewport.voi.windowCenter = wl;
    enabledElement.viewport.voi.windowWidth = ww;

    const modality = _.get(image, "modality", "");

    if (modality.toLowerCase() === "pt") {
      const seriesMinMax = metadataCache.getSeriesMinMax(this.props.data);
      const totalMax = seriesMinMax["max"];
      const imageMax = image.minMax[1];

      const percent = (imageMax / totalMax) * 100;
      //const slope = enabledElement.image.rescaleSlope;
      const seriesMaxRescaleSlope = metadataCache.getSeriesMaxRescaleSlope(
        this.props.data
      );
      if (seriesMaxRescaleSlope === null) {
        return;
      }

      const supremumUnit = ((totalMax * percent) / 100) * seriesMaxRescaleSlope;
      const infimumUnit = 0 * seriesMaxRescaleSlope;

      const windowCenter = (supremumUnit + infimumUnit) / 2;
      const windowWidth = Math.abs(supremumUnit - infimumUnit);
      enabledElement.viewport.voi.windowCenter = windowCenter;
      enabledElement.viewport.voi.windowWidth = windowWidth;
    }

    this.state.presetLevelName = "F";

    this.setState(this.state);
    if (this.element !== null && this.element !== undefined) {
      let enabledElement = getEnabledElement(this.element);
      enabledElement.needsRedraw = true;
    }
    CustomEvent.triggerEvent(
      this.element,
      CustomEvent.EVENTS.WWWC_CHANGED,
      enabledElement
    );
  };

  /**
   * @description 현재 viewport Active모드 변경
   * @deprecated
   * @method setViewportActive
   * @memberof Viewport
   */
  setViewportActive = (modifiedKeys) => {
    // 키 + 마우스 조합에 따른, DCMView선택 제어 수행s
    const { gridLayoutStore, synchronizationStore, identifier } = this.props;
    //gridLayoutStore.setActiveDCMViewIndex(identifier);
    gridLayoutStore.activeMultiDCMView(modifiedKeys, identifier);
    if (synchronizationStore.isSupportedSynchronizationOnActive() === false) {
      synchronizationStore.disableSyncState();
    }
  };

  /**
   * @description viewport 설정사항 변경
   * @method setViewport
   * @memberof Viewport
   */

  handleWindowResize = (event) => {
    // HACK : DCMView 크기 변화 발생시 DCMTag를 표시하는 폰트 크기를 변화시키기위해
    // 아래와 같이 setState를 실행하여 리-렌더링을 유도하게 하였음
    //this.setState({}); // 리사이즈시 속도 개선을위해 주석
    this.debouncedResize();
  };

  handleSetViewPositionInitialize = (paylaod, msg) => {
    const { data } = this.props;

    if (!this.props.isActive) {
      return;
    }

    if (data === undefined || data === null) {
      return;
    }
    let enabledElement = getEnabledElement(this.element);
    enabledElement.viewport.scale = 1.0;
    enabledElement.viewport.translation.x = 0;
    enabledElement.viewport.translation.y = 0;

    Canvas.resize(this.element, true);

    enabledElement.needsRedraw = true;
  };

  handleSetScrollIndexAdd = (payload, msg) => {
    const { force, id } = payload;
    if (!this.props.isActive && !(force && this.props.identifier === id)) {
      return;
    }

    const { displacement } = payload;
    const moveIndex = displacement;
    const { data, displayRange } = this.props;
    const { currentImageIdx } = this.state;
    let newImageIndex = currentImageIdx + moveIndex;

    if (data === undefined || data === null) {
      return;
    }

    let metadataCache = new Cache.Metadata();
    //const numberOfImages = metadataCache.getNumOfFrame(data);
    const numberOfImages = displayRange["max"];
    // 스크롤이 가능한 범위의 경계에서 범위를 넘어서는 방향으로 스크롤 이동이
    // 발생한 경우, 설정에 따라 스크롤 결과 값이 결정.
    const { scrollLoop } = this.props;
    if (scrollLoop === false) {
      // Scroll Loop 기능이 활성화 되지 않은 경우, 스크롤 위치를 경계값으로 지정한다.
      newImageIndex = Math.range.clip(newImageIndex, 0, numberOfImages - 1);
    } else {
      // Scroll Loop 기능이 활성화된 경우, 시작 스크롤 위치로 이동한다.
      newImageIndex = newImageIndex % (numberOfImages + 1);
      //newImageIndex = (numberOfImages + newImageIndex) % numberOfImages;
      if (newImageIndex < 0) {
        newImageIndex = numberOfImages + newImageIndex;
      }
    }
    const isNotChange = currentImageIdx === newImageIndex;
    const prevRows = metadataCache.getRows(data, currentImageIdx);
    const prevCols = metadataCache.getColumns(data, currentImageIdx);

    const newRows = metadataCache.getRows(data, newImageIndex);
    const newCols = metadataCache.getColumns(data, newImageIndex);
    let enabledElement = getEnabledElement(this.element);
    const changeSize =
      prevRows !== newRows || prevCols !== newCols ? true : false;
    if (isNotChange === false && changeSize === true) {
      //resize(this.element, true);
      enabledElement.viewport.displayedArea.brhc.x = newCols;
      enabledElement.viewport.displayedArea.brhc.y = newRows;
      enabledElement.viewport.translation.x = 0;
      enabledElement.viewport.translation.y = 0;
      enabledElement.viewport.rotation = 0;
      enabledElement.viewport.scale = 1;
      enabledElement.image = metadataCache.getFrame(data, newImageIndex);
      resize(this.element, true);
    }
    enabledElement.imageIdx = newImageIndex;
    this.setState({
      currentImageIdx: newImageIndex,
    });

    CustomEvent.triggerEvent(
      this.element,
      CustomEvent.EVENTS.IMAGE_RENDERED,
      enabledElement
    );

    if (isNotChange === false) {
      enabledElement.needsRedraw = true;
      CustomEvent.triggerEvent(
        this.element,
        CustomEvent.EVENTS.SCROLL_INDEX_CHANGED,
        newImageIndex
      );
    }
  };

  /**
   * @description viewport에서 스크롤링시 발생하는 이벤트 처리 핸들러
   * @method handleStackScroll
   * @memberof Viewport
   */
  handleStackScroll = (event) => {
    let { data, displayRange, isMPR } = this.props;
    let enabledElement = getEnabledElement(this.element);
    let currentImageIdx = enabledElement.imageIdx;
    //const { currentImageIdx } = this.state;

    let eventData = event.detail;
    const moveIndex = -eventData.direction;

    let newImageIndex = currentImageIdx + moveIndex;

    if (data === undefined || data === null) {
      return;
    }

    let metadataCache = new Cache.Metadata();
    //const numberOfImages = metadataCache.getNumOfFrame(data);
    const numberOfImages = displayRange["max"];

    // 스크롤이 가능한 범위의 경계에서 범위를 넘어서는 방향으로 스크롤 이동이
    // 발생한 경우, 설정에 따라 스크롤 결과 값이 결정.
    const { scrollLoop } = this.props;
    if (scrollLoop === false) {
      // Scroll Loop 기능이 활성화 되지 않은 경우, 스크롤 위치를 경계값으로 지정한다.
      newImageIndex = Math.range.clip(newImageIndex, 0, numberOfImages - 1);
    } else {
      // Scroll Loop 기능이 활성화된 경우, 시작 스크롤 위치로 이동한다.
      newImageIndex = newImageIndex % (numberOfImages + 1);
      //newImageIndex = (newImageIndex) % (numberOfImages + 1);
      if (newImageIndex < 0) {
        newImageIndex = numberOfImages + newImageIndex;
      }
    }

    newImageIndex = Math.max(
      Math.min(newImageIndex, displayRange["max"]),
      displayRange["min"]
    );
    const isNotChange = currentImageIdx === newImageIndex;
    const prevRows = metadataCache.getRows(data, currentImageIdx);
    const prevCols = metadataCache.getColumns(data, currentImageIdx);

    const newRows = metadataCache.getRows(data, newImageIndex);
    const newCols = metadataCache.getColumns(data, newImageIndex);
    const changeSize =
      prevRows !== newRows || prevCols !== newCols ? true : false;
    if (isNotChange === false && changeSize === true) {
      //resize(this.element, true);
      enabledElement.viewport.displayedArea.brhc.x = newCols;
      enabledElement.viewport.displayedArea.brhc.y = newRows;
      enabledElement.viewport.translation.x = 0;
      enabledElement.viewport.translation.y = 0;
      enabledElement.viewport.rotation = 0;
      enabledElement.viewport.scale = 1;
      enabledElement.image = metadataCache.getFrame(data, newImageIndex);
      resize(this.element, true);
    }
    const image = enabledElement.image;
    if (!!image === false) {
      return;
    }
    const imageOrientation =
      image.imageOrientationPatient || image.imageOrientation;
    //if (_.isEqual(imageOrientation, [1, 0, 0, 0, 1, 0]) === false) {
    if (isMPR === true) {
      image.width = image.columns;
      image.height = image.rows;
      const iop = imageOrientation;
      const ipp = _.cloneDeep(image.imagePosition);
      const rowCosines = vec3.fromValues(iop[0], iop[1], iop[2]);
      const colCosines = vec3.fromValues(iop[3], iop[4], iop[5]);
      let zedCosines = vec3.create();

      vec3.cross(zedCosines, rowCosines, colCosines);

      //Update position in the Zed direction
      const stepSize = image.pixelSpacing[2] || 1;
      const dir = 1;
      let dx = zedCosines[0] * stepSize * (dir * -1) * moveIndex;
      let dy = zedCosines[1] * stepSize * dir * moveIndex;
      let dz = zedCosines[2] * stepSize * (dir * -1) * moveIndex;

      ipp[0] += dx;
      ipp[1] += dy;
      ipp[2] += dz;
      // image.imagePosition[0] = ipp[0];
      // image.imagePosition[1] = ipp[1];
      // image.imagePosition[2] = ipp[2];
      const bounds = metadataCache.getImagePatientPositionBounds(data);
      image.imagePosition[0] = math.min(math.max(ipp[0], bounds[0]), bounds[1]);
      image.imagePosition[1] = math.min(math.max(ipp[1], bounds[2]), bounds[3]);
      image.imagePosition[2] = math.min(math.max(ipp[2], bounds[4]), bounds[5]);

      // const tool = getToolForElement(this.element, "crosshair3D");
      event.pageX = event.detail.pageX;
      event.pageY = event.detail.pageY;
      CustomEvent.triggerEvent(this.element, "mousemove", event);
    }

    enabledElement.imageIdx = newImageIndex;
    currentImageIdx = newImageIndex;
    this.setState({
      //currentImageIdx: currentImageIdx,
      currentImageIdx: newImageIndex,
    });

    enabledElement.imageIdx = newImageIndex;
    if (isNotChange === false) {
      enabledElement.needsRedraw = true;
      CustomEvent.triggerEvent(
        this.element,
        CustomEvent.EVENTS.SCROLL_INDEX_CHANGED,
        newImageIndex
      );
    }
  };

  /**
   * @description 마우스 클릭 이벤트 발생시 처리 핸들리
   * @method handleMouseClick
   * @param event 이벤트 발생 정보
   * @memberof Viewport
   */
  // 실험
  isDragging = false;
  /**
   * @description 마우스 클릭 이벤트 발생시 처리 핸들리
   * @method handleMouseClick
   * @param event 이벤트 발생 정보
   * @memberof Viewport
   */
  handleMouseClick = (event) => {
    const { synchronizationStore, isActive } = this.props;
    const { altKey, ctrlKey, shiftKey } = event;
    const syncState = synchronizationStore.getSyncState();
    if (
      syncState === Constants.Synchronization.SYNC_MANUAL &&
      isActive === true
    ) {
      this.isDragging = false;
      this.element.addEventListener("mousemove", this.handleMouseMove);
      this.element.addEventListener("mouseup", this.handleMouseup);
      return;
    }

    // 마우스 클릭에 따른 DCMView 활성 동작 수행
    this.setViewportActive({
      altKey: altKey,
      ctrlKey: ctrlKey,
      shiftKey: shiftKey,
    });
  };
  handleMouseMove = (event) => {
    this.isDragging = true;
    this.element.removeEventListener("mousemove", this.handleMouseMove);
  };
  handleMouseup = (event) => {
    const { altKey, ctrlKey, shiftKey } = event;
    if (this.isDragging === false) {
      // 마우스 클릭에 따른 DCMView 활성 동작 수행
      this.setViewportActive({
        altKey: altKey,
        ctrlKey: ctrlKey,
        shiftKey: shiftKey,
      });
    }
    this.isDragging = false;
    this.element.removeEventListener("mouseup", this.handleMouseup);
  };
  /**
   * @description 마우스 더블클릭 이벤트 발생시 처리 핸들러
   * @method handleMouseDblClick
   * @param event 이벤트 발생 정보
   * @memberof Viewport
   */
  handleMouseDblClick = (event) => {
    const { data, identifier, gridLayoutStore, synchronizationStore } =
      this.props;
    ///
    /// 0. 데이터가 없는 View에 대해서는 동작하지 않는다.
    ///
    if (data === undefined || data === null) {
      return;
    }

    ///
    /// 1. activate viewport
    ///
    gridLayoutStore.setActiveDCMViewIndex(identifier);

    ///
    /// 2. publish view one viewport
    ///
    gridLayoutStore.focusOneLayout(identifier);
    if (synchronizationStore.isSupportedSynchronizationOnActive() === true) {
      synchronizationStore.reactiveSyncState();
    } else {
      synchronizationStore.disableSyncState();
    }
  };

  /**
   * @description scroll 이동 이벤트 발생시, 현재 스크롤 위치에
   * 맞는 DICOM 영상으로 갱신한다.
   * @method handleChangeScroll
   * @param event 스크롤 이벤트 정보
   * @param data
   * @memberof Viewport
   */
  handleChangeScroll = function (event, payload) {
    let eventData = event.detail;
    let enabledElement = getEnabledElement(this.element);
    //let { currentImageIdx } = this.state;

    let currentImageIdx = enabledElement.imageIdx;
    let { data, displayRange, gridLayoutStore } = this.props;

    let metadataCache = new Cache.Metadata();

    let numOfFrame = 0;
    if (data === null || data === undefined) {
      data = gridLayoutStore.getMetadataIDOnDCMView(this.props.identifier);
      //numOfFrame = metadataCache.getNumOfFrame(data);
    } else {
      //numOfFrame = metadataCache.getNumOfFrame(data);
    }
    numOfFrame = displayRange["max"];

    const newScrollIndex = eventData.newImageIndex;
    let newImageIdIndex = newScrollIndex;
    // 스크롤이 가능한 범위의 경계에서 범위를 넘어서는 방향으로 스크롤 이동이
    // 발생한 경우, 설정에 따라 스크롤 결과 값이 결정.
    const { scrollLoop } = this.props;
    if (scrollLoop === false) {
      // Scroll Loop 기능이 활성화 되지 않은 경우, 스크롤 위치를 경계값으로 지정한다.
      newImageIdIndex = Math.range.clip(newScrollIndex, 0, numOfFrame - 1);
    } else {
      // Scroll Loop 기능이 활성화된 경우, 시작 스크롤 위치로 이동한다.
      //newImageIdIndex = (numOfFrame + newScrollIndex) % numOfFrame; //clip(newImageIndex, 0, numberOfImages - 1);
      newImageIdIndex = newScrollIndex % (numOfFrame + 1);
      if (newImageIdIndex < 0) {
        newImageIdIndex = numOfFrame + newImageIdIndex;
      }
    }
    newImageIdIndex = Math.max(
      Math.min(newImageIdIndex, displayRange["max"]),
      displayRange["min"]
    );

    // check image width/height change
    const isNotChange = currentImageIdx === newImageIdIndex;
    const prevRows = metadataCache.getRows(data, currentImageIdx);
    const prevCols = metadataCache.getColumns(data, currentImageIdx);

    const newRows = metadataCache.getRows(data, newImageIdIndex);
    const newCols = metadataCache.getColumns(data, newImageIdIndex);

    const changeSize =
      prevRows !== newRows || prevCols !== newCols ? true : false;
    if (isNotChange === false && changeSize === true) {
      //resize(this.element, true);
      enabledElement.viewport.displayedArea.brhc.x = newCols;
      enabledElement.viewport.displayedArea.brhc.y = newRows;
      enabledElement.viewport.translation.x = 0;
      enabledElement.viewport.translation.y = 0;
      enabledElement.viewport.rotation = 0;
      enabledElement.viewport.scale = 1;
      //resize(this.element, false, true);
      enabledElement.image = metadataCache.getFrame(data, newImageIdIndex);
      resize(this.element, true);
    }
    // Frame 범위 한정
    // let frameRangeMin = frameRange["min"];
    // let frameRangeMax = frameRange["max"];
    // displayRange["min"] = Math.min(
    //   Math.max(displayRange["min"], 0),
    //   numOfFrame - 1
    // );
    // displayRange["max"] = Math.max(
    //   Math.min(displayRange["max"], numOfFrame - 1),
    //   displayRange["min"]
    // );

    currentImageIdx = newImageIdIndex;
    this.setState({
      currentImageIdx: currentImageIdx,
    });

    if (enabledElement != null) {
      enabledElement.imageIdx = currentImageIdx;
      enabledElement.needsRedraw = true;
    }

    CustomEvent.triggerEvent(
      this.element,
      CustomEvent.EVENTS.IMAGE_RENDERED,
      enabledElement
    );

    let noSync = eventData.noSync;
    if (noSync !== undefined && noSync != null && noSync === true) {
      return;
    }

    CustomEvent.triggerEvent(
      this.element,
      CustomEvent.EVENTS.SCROLL_INDEX_CHANGED,
      newImageIdIndex
    );
  };

  /**
   * @method handleContextMenu
   * @description 우 클릭시 발생하는 기본 동작을 막는다.
   * @memberof Viewport
   */
  handleContextMenu = (e) => {
    // 우측 마우스를 눌렀을때, 발생하는 기본 동작을 막는다.
    e.preventDefault();
  };

  /**
   * @method getContextMenuId
   * @description Context Menu의 ID 리턴
   * @returns Context Menu의 ID 리턴
   * @memberof Viewport
   */
  getContextMenuId() {
    const { data } = this.props;
    const { identifier } = this.props;

    if (typeof data === undefined) {
      return "viewport-menuID-none";
    }
    if (data === undefined || data === null) {
      return "viewport-menuID-none";
    }
    return "viewport-menuID-" + identifier;
  }

  /**
   * @method getContextMenuId
   * @description Context Menu의 ID 리턴
   * @returns Context Menu의 ID 리턴
   * @memberof Viewport
   */
  getToolContextMenuId() {
    const { data } = this.props;
    const { identifier } = this.props;

    if (typeof data === undefined) {
      return "viewport-tool-menuID-none";
    }
    if (data === undefined || data === null) {
      return "viewport-tool-menuID-none";
    }
    return "viewport-tool-menuID-" + identifier;
  }

  /**
   * @method getActiveClass
   * @description Active상태인 경우(마우스로 클릭한 상태), HTML Class Name으로 active 리턴
   * Active 상태가 아니면, 빈 문자열 리턴
   * @returns
   *    UI가 Active 상태 - "active"
   *    UI가 Non-Active 상태 - ""
   * @memberof Viewport
   */
  getActiveClass() {
    const { isActive } = this.props;
    if (isActive === true) {
      return "active";
    } else {
      return "";
    }
  }

  getImage = () => {
    const { data } = this.props;
    if (data === undefined || data === null) {
      return null;
    }

    let index =
      this.state.currentImageIdx === -1 ? 0 : this.state.currentImageIdx;
    let metadataCache = new Cache.Metadata();
    const image = metadataCache.getFrame(data, index);

    return image;
  };

  getImageIndex = () => {
    const { data } = this.props;
    if (data === undefined || data === null) {
      return null;
    }

    let imageIndex =
      this.state.currentImageIdx === -1 ? 0 : this.state.currentImageIdx;

    return imageIndex;
  };

  getValue = () => {
    const { data } = this.props;
    if (data === undefined || data === null) {
      return "";
    }

    let index =
      this.state.currentImageIdx === -1 ? 0 : this.state.currentImageIdx;
    let metadataCache = new Cache.Metadata();
    const image = metadataCache.getFrame(data, index);

    //let image = imageSet.stack[0].frame[index];
    if (image === undefined || image === null) {
      return "";
    }
    if (this.element.current === null) {
      return "";
    }

    const { mouseXPos, mouseYPos } = this.state;
    const text = getPixelValue(mouseXPos, mouseYPos, this.element, image.color);
    return text;
  };

  getLevelPresetName = () => {
    const { gridLayoutStore, identifier } = this.props;
    //const selectedValue = "";
    const selectedValue = gridLayoutStore.getContrastOptionName(identifier);
    //const selectedValue = gridLayoutStore.getContrastOptionNameOnActiveDCMView();
    if (typeof selectedValue !== "string") {
      return "";
    }
    if (selectedValue === "") {
      return "";
    }
    this.state.presetLevelName = selectedValue;

    return "[" + selectedValue + "]";
  };

  /**
   * @method handleRemoveData
   * @description viewport에 데이터 삭제 명령시, 처리 핸들러
   * @todo Pub/Sub 사용 걷어내야함.
   * @memberof Viewport
   */
  handleRemoveData = (event, removedData) => {
    const { synchronizationStore, toolbarStore } = this.props;
    /// 교체 상황 발생
    const parameters = {
      title: "Warning",
      message: Constants.Strings.DialogMessage.ConfirmUnloadDataOnDCMView,
      buttonType: "YESNO",
      hasCloseButton: false,
      hasTodayOffButton: false,
      handleYesClick: function () {
        this.removeData(removedData);
        if (
          synchronizationStore.isSupportedSynchronizationOnActive() === true
        ) {
          synchronizationStore.reactiveSyncState();
        } else {
          synchronizationStore.disableSyncState();
        }

        setToolDisabledForElement(this.element, "ImageFusion", {
          synchronizationContext:
            Config.rootStore.synchronizationStore._updateImageSync,
        });
        toolbarStore.disableImageFusion();
      }.bind(this),
    };
    FloatMessageBox(parameters);
  };

  removeData = (removeData) => {
    const { data } = this.props;
    const { identifier, gridLayoutStore, synchronizationStore } = this.props;
    ///
    /// 데이터가 없는경우, 예외처리
    ///
    if (typeof data === undefined) {
      return;
    }

    ///
    /// viewport 설정 정보를 제거한다.
    ///
    const viewportId = identifier;
    this.setState({
      viewport: getDefaultViewport(null, undefined),
    });

    ///
    /// 데이터 삭제 메세지 전달
    ///
    gridLayoutStore.clearDCMView(viewportId);
    clearElementState(this.element);
    if (synchronizationStore.isSupportedSynchronizationOnActive() === true) {
      synchronizationStore.reactiveSyncState();
    } else {
      synchronizationStore.disableSyncState();
    }
    let enabledElement = getEnabledElement(this.element);
    enabledElement.imageIdx = 0;
    enabledElement.image = null;
    enabledElement.viewport = getDefaultViewport(null, undefined);
    enabledElement.needsRedraw = true;
  };

  handleClearAllView = (removedData, event) => {
    const { synchronizationStore, toolbarStore } = this.props;
    const parameters = {
      title: "Warning",
      message: Constants.Strings.DialogMessage.ConfirmUnloadAllDataOnDCMView,
      buttonType: "YESNO",
      hasCloseButton: false,
      hasTodayOffButton: true,
      handleYesClick: function () {
        this.clearAllView(removedData);
        if (
          synchronizationStore.isSupportedSynchronizationOnActive() === true
        ) {
          synchronizationStore.reactiveSyncState();
        } else {
          synchronizationStore.disableSyncState();
        }
        toolbarStore.disableImageFusionForAll();
      }.bind(this),
    };
    FloatMessageBox(parameters);
  };

  clearAllView = () => {
    const { gridLayoutStore } = this.props;
    ///
    /// 데이터 삭제 메세지 전달
    ///
    gridLayoutStore.clearAll();
    clearAll();
    redrawAllEnabledElement();
  };

  /**
   * @description 데이터를 캔버스에 비동기로 그린다.
   * @method renderCanvasAsync
   *
   * @returns
   * @memberof Viewport
   */
  renderCanvasAsync() {
    const domNode = ReactDOM.findDOMNode(this);
    const canvasElement = domNode.querySelectorAll("canvas")[0];
    const { data, imageOrientation, isMPR, identifier } = this.props;
    //const { currentImageIdx } = this.state;
    let { currentImageIdx } = this.state;
    let enabledElement = getEnabledElement(this.element);
    const imageIdx = enabledElement.imageIdx;
    if ((data === undefined || data === null) && enabledElement.needsRedraw) {
      const context = canvasElement.getContext("2d");
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.fillStyle = "black";
      context.fillRect(0, 0, canvasElement.width, canvasElement.height);

      enabledElement.needsRedraw = false;

      return null;
    }

    if (!enabledElement.needsRedraw) {
      return null;
    }

    return new Promise((resolve, reject) => {
      try {
        let { displayRange } = this.props;
        let metadataCache = new Cache.Metadata();

        const clipImageIdx = Math.max(
          Math.min(imageIdx, displayRange["max"]),
          displayRange["min"]
        );

        //
        let enabledElement = getEnabledElement(this.element);
        let image = enabledElement.image;
        const imageOrientation =
          _.get(image, "imageOrientation", null) ||
          _.get(image, "imageOrientationPatient", null) ||
          this.props.imageOrientation;

        //if (_.isEqual(imageOrientation, [1, 0, 0, 0, 1, 0]) === false) {
        if (isMPR === true) {
          const originAxialImage = metadataCache.getFrame(data, 0);
          const resliceImage = enabledElement.image;
          // axial 이 아닌경우
          if (!originAxialImage) {
            resolve();
            return;
          }

          if (!!resliceImage === false) {
            let mprImage = createMPRImage(originAxialImage, {
              imageOrientation: imageOrientation,
              //imagePosition: originAxialImage.imagePosition,
              imagePosition: "center",
            });

            image = mprImage;
            enabledElement.image = image;
            const { onInitCallback } = this.props;
            if (typeof onInitCallback === "function") {
              onInitCallback(identifier);
            }
            // @NOTE MPR 최초 로드시, Size rescale 이슈
            resize(this.element, true);
          } else {
            let mprImage = createMPRImage(originAxialImage, {
              imageOrientation: imageOrientation,
              imagePosition: resliceImage.imagePosition,
            });
            if (!!image.mprColor === true) {
              mprImage.mprColor = image.mprColor;
            }
            image = mprImage;
            enabledElement.image = image;
            image.imagePosition = resliceImage.imagePosition;
          }
        } else {
          let metadataCache = new Cache.Metadata();
          let image = metadataCache.getFrame(this.props.data, currentImageIdx);
          if (!image) {
            resolve();
            return;
          }
        }

        const uuid = enabledElement.uuid;

        enabledElement = renderToCanvas(
          canvasElement,
          image,
          enabledElement.viewport,
          uuid,
          clipImageIdx
        );
        enabledElement.element = this.element;

        enabledElement.needsRedraw = false;
        updateEnabledElement(enabledElement);

        CustomEvent.triggerEvent(
          this.element,
          CustomEvent.EVENTS.IMAGE_RENDERED,
          enabledElement
        );
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * @method renderViewportOverlay
   * @description DICOM Tag Overlay UI 컴포넌트 렌더링
   * @returns
   * @memberof Viewport
   */
  renderViewportOverlay() {
    const { isDisplayDCMTags, layoutString, identifier } = this.props;
    if (!isDisplayDCMTags) {
      return false;
    }

    if (
      this.element.clientHeight === null ||
      this.element.clientHeight === undefined
    ) {
      return false;
    }
    const { data, displayRange } = this.props;

    // 코드 실험
    let { currentImageIdx } = this.state;
    currentImageIdx = Math.range.clip(
      currentImageIdx,
      displayRange["min"],
      displayRange["max"]
    );
    let enabledElement = getEnabledElement(this.element);
    currentImageIdx = Math.range.clip(
      enabledElement.imageIdx,
      displayRange["min"],
      displayRange["max"]
    );

    if (data !== undefined && data !== null) {
      let metadataCache = new Cache.Metadata();
      const numOfFrame = metadataCache.getNumOfFrame(this.props.data);
      const canvas = getCanvas(this.element);
      return (
        <DCMTagMarkerContainer
          ref={(o) => (this.tagMarker = o)}
          innerRef={(o) => {
            this.tagInnerMarker = o;
          }}
          isFocus={this.props.focus}
          idx={identifier}
          number={this.props.identifier}
          viewport={enabledElement.viewport}
          frameRange={{ min: displayRange["min"], max: displayRange["max"] }}
          imageIndex={currentImageIdx}
          numImages={displayRange["max"] - displayRange["min"] + 1}
          //numImages={numOfFrame}
          stack={data}
          layoutString={layoutString}
          parentElement={canvas}
          width={this.props.width}
          height={this.props.height}
          value={this.getValue()}
          levelPresetName={this.getLevelPresetName()}
        />
      );
    }
    return;
  }

  renderOrientationMarkers = () => {
    const {
      data,
      displayRange,
      focus,
      width,
      height,
      isDisplayOrientationMarkers,
    } = this.props;
    if (!isDisplayOrientationMarkers || !data) {
      return;
    }
    let metadataCache = new Cache.Metadata();
    const rowCosines = metadataCache.getRowCosines(this.props.data);
    const columnCosines = metadataCache.getColumnCosines(this.props.data);

    let viewport = getDefaultViewport(null, undefined);
    if (hasEnabledElement(this.element)) {
      viewport = getEnabledElement(this.element);
    }
    return (
      <OrientationMarkerContainer
        rowCosines={rowCosines}
        columnCosines={columnCosines}
        hflip={viewport.hflip}
        vflip={viewport.vflip}
        rotation={viewport.rotation}
        frameRangeMin={displayRange["min"]}
        frameRangeMax={displayRange["max"]}
        width={width}
        height={height}
        isFocus={focus}
      />
    );
  };

  /**
   *
   *
   * @memberof Viewport
   */
  renderContextMenu = () => {
    var renderMenu = [];

    if (this.props.data !== undefined && this.props.data !== null) {
      renderMenu = [
        <MenuItem key={0} data={{}} onClick={this.handleRemoveData}>
          Unload it
        </MenuItem>,
        <MenuItem key={1} data={{ foo: "bar" }} divider={true} />,
        <MenuItem key={2} data={{}} onClick={this.handleClearAllView}>
          Unload all
        </MenuItem>,
      ];
      return (
        <ContextMenu
          className={cx("noselect", "nodrag")}
          id={this.getContextMenuId()}
        >
          {renderMenu}
        </ContextMenu>
      );
    }

    return "";
  };

  renderViewport = () => {
    let className = "removable";
    let { identifier, showTagOverlay, isMPR, colorBox } = this.props;
    // let enabledElement = getEnabledElement(this.element);
    // const mprColor = _.get(enabledElement, "image.mprColor", null);
    return (
      <React.Fragment>
        <div className={cx(className, "noselect", "nodrag")}>
          <div
            id={"viewer-" + identifier}
            tabIndex={identifier}
            className={
              "imageViewerViewport viewport-element " + this.getActiveClass()
            }
            onContextMenu={this.handleContextMenu}
            ref={(input) => {
              this.element = input;
            }}
          >
            <canvas className="viewer-canvas" />
          </div>
          <div className="viewport-color-box" />
          {isMPR && !!colorBox && (
            <ColorBox prefix="viewport" color={colorBox} />
          )}
          {showTagOverlay && this.renderViewportOverlay()}
          {showTagOverlay && this.renderOrientationMarkers()}
        </div>
      </React.Fragment>
    );
  };

  /**
   *
   *
   * @memberof Viewport
   */
  render = () => {
    let { hasContextMenu } = this.props;
    if (hasContextMenu) {
      return (
        <React.Fragment>
          <ContextMenuTrigger id={this.getContextMenuId()} holdToDisplay={-1}>
            {this.renderViewport()}
          </ContextMenuTrigger>
          {this.renderContextMenu()}
        </React.Fragment>
      );
    } else {
      return <React.Fragment>{this.renderViewport()}</React.Fragment>;
    }
  };
}

export default Viewport;
